import { useState } from 'react';
import { Lock, Mail, CheckCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';

export default function ForgotPassword() {
    const { t } = useTranslation();
    const [email, setEmail] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState<string>('');

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError('');

        if (!email) {
            setError(t('forgotPasswordPage.validation.emailRequired'));
            return;
        }

        if (!validateEmail(email)) {
            setError(t('forgotPasswordPage.validation.invalidEmail'));
            return;
        }

        setIsLoading(true);

        try {
            const res = await fetch(API_ENDPOINTS.FORGOT_PASSWORD, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.detail || t('forgotPasswordPage.validation.sendFailed'));
            }

            setIsSubmitted(true);
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message);
            } else {
                setError(t('forgotPasswordPage.validation.genericError'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendEmail = () => {
        setIsSubmitted(false);
        setEmail('');
    };

    if (isSubmitted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div className="text-center">
                        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 animate-pulse">
                            <CheckCircle className="h-8 w-8 text-green-600" />
                        </div>
                        <h2 className="mt-6 text-3xl font-bold text-gray-900">
                            {t('forgotPasswordPage.checkEmail')}
                        </h2>
                        <p className="mt-2 text-sm text-gray-600">
                            {t('forgotPasswordPage.resetInstructions')}{' '}
                            <span className="font-medium text-gray-900">{email}</span>
                        </p>
                    </div>

                    <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-200">
                        <div className="space-y-4">
                            <div className="text-sm text-gray-600">
                                <p>{t('forgotPasswordPage.didNotReceive')}</p>
                            </div>

                            <button
                                onClick={handleResendEmail}
                                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-150 ease-in-out"
                            >
                                {t('forgotPasswordPage.tryAnotherEmail')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8">
                <div className="text-center">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary/10">
                        <Lock className="h-8 w-8 text-primary" />
                    </div>
                    <h2 className="mt-6 text-3xl font-bold text-gray-900">
                        {t('forgotPasswordPage.title')}
                    </h2>
                    <p className="mt-2 text-sm text-gray-600">
                        {t('forgotPasswordPage.subtitle')}
                    </p>
                </div>

                <div className="bg-white py-8 px-6 shadow-xl rounded-lg border border-gray-200">
                    <div className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                {t('forgotPasswordPage.email')}
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (error) setError('');
                                    }}
                                    className={`block w-full pl-10 pr-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm transition duration-150 ease-in-out bg-white text-gray-900 ${error
                                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                                        : 'border-gray-300'
                                        }`}
                                    placeholder={t('forgotPasswordPage.emailPlaceholder')}
                                />
                            </div>
                            {error && (
                                <p className="mt-2 text-sm text-red-600" role="alert">
                                    {error}
                                </p>
                            )}
                        </div>

                        <div>
                            <form onSubmit={handleSubmit}>
                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition duration-150 ease-in-out shadow-sm"
                                >
                                    {isLoading ? (
                                        <>
                                            <div className="animate-spin -ml-1 mr-3 h-5 w-5 text-white">
                                                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            </div>
                                            {t('forgotPasswordPage.sending')}
                                        </>
                                    ) : (
                                        t('forgotPasswordPage.sendResetLink')
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}